import React from "react";
import { Link } from "react-router-dom";
import { usePlayerProfile } from "@/hooks/usePlayerProfile";
import { extractDiscordIdentity } from "@/lib/discordIdentity";
import SteamIdBlock from "./SteamIdBlock";
import { useI18n } from "@/i18n/I18nProvider";
import "./RequirePlayerAuth.css";

const DiscordGlyph = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037 12.3 12.3 0 0 0-.608 1.25 18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C1.566 7.71.96 10.98 1.194 14.23a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107 14.322 14.322 0 0 0 1.225 1.993.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.36-3.66-.61-6.84-2.545-9.83a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
);

/**
 * @param {{
 *   user: import('@supabase/supabase-js').User | null | undefined;
 *   onLoginClick: () => void;
 *   title?: string;
 *   children: React.ReactNode;
 * }} props
 */
export default function RequirePlayerAuth({ user, onLoginClick, title, children }) {
    const { profile, loading, savingSteam, saveSteamId, needsLogin, needsSteam, hasSteamId } =
        usePlayerProfile(user);
    const { t } = useI18n();

    if (loading && !profile) {
        return (
            <div className="zrs-auth-gate zrs-auth-gate--loading">
                <p>{t("auth.gate.checking")}</p>
            </div>
        );
    }

    if (needsLogin) {
        return (
            <div className="zrs-auth-gate">
                <div className="zrs-auth-gate__card">
                    <h1 className="zrs-auth-gate__title">{title || t("auth.gate.restrictedTitle")}</h1>
                    <p className="zrs-auth-gate__lead">
                        {t("auth.gate.leadBeforeDiscord")}
                        <strong>Discord</strong>
                        {t("auth.gate.leadBeforeSteam")}
                        <strong>SteamID</strong>
                        {t("auth.gate.leadAfterSteam")}
                    </p>
                    <button type="button" className="zrs-auth-gate__discord-btn" onClick={onLoginClick}>
                        <DiscordGlyph />
                        {t("auth.gate.discordLogin")}
                    </button>
                    <Link to="/" className="zrs-auth-gate__back">
                        {t("auth.gate.backToSite")}
                    </Link>
                </div>
            </div>
        );
    }

    if (needsSteam) {
        const discord = extractDiscordIdentity(user, profile);
        return (
            <div className="zrs-auth-gate">
                <div className="zrs-auth-gate__card zrs-auth-gate__card--wide">
                    <h1 className="zrs-auth-gate__title">{t("auth.gate.steamRequiredTitle")}</h1>
                    <p className="zrs-auth-gate__lead">
                        {t("auth.gate.steamRequiredLead")}
                    </p>
                    <div className="zrs-auth-gate__discord-preview">
                        {discord.discordAvatarUrl ? (
                            <img src={discord.discordAvatarUrl} alt="" className="zrs-auth-gate__avatar" />
                        ) : (
                            <span className="zrs-auth-gate__avatar zrs-auth-gate__avatar--placeholder" aria-hidden>
                                <DiscordGlyph />
                            </span>
                        )}
                        <div>
                            <div className="zrs-auth-gate__discord-name">{discord.discordGlobalName}</div>
                            <div className="zrs-auth-gate__discord-tag">{discord.discordTag}</div>
                        </div>
                    </div>
                    <SteamIdBlock
                        steamId={profile?.steam_id}
                        saving={savingSteam}
                        onSave={saveSteamId}
                    />
                    <Link to="/conta" className="zrs-auth-gate__back">
                        {t("auth.gate.goToAccount")}
                    </Link>
                </div>
            </div>
        );
    }

    if (!hasSteamId) {
        return null;
    }

    return children;
}
